/**
 * Capitalize the first letter of every word in a string, leaving the rest of each word untouched.
 *
 * @example
 *   ```ts
 *   capitalize("law firm");
 *   // => "Law Firm"
 *   ```;
 *
 * @param text - The string to title-case. Falsy values are returned as-is.
 * @returns The input with the first letter of each whitespace-separated word upper-cased.
 */
export const capitalize = (text: string): string =>
  text ? text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1)) : text;

/**
 * Naive English pluralization for a singular noun, matching the same heuristic the backend uses
 * when scoping name-matching to the entity type inferred from a query.
 *
 * This is a heuristic, not a full pluralization engine — it does not handle irregular nouns (e.g.
 * "goose" -> "geese").
 *
 * @example
 *   ```ts
 *   pluralize("law firm");
 *   // => "law firms"
 *   pluralize("agency");
 *   // => "agencies"
 *   ```;
 *
 * @param word - The singular noun to pluralize. Falsy values are returned as-is.
 * @returns The pluralized form of `word`.
 */
export const pluralize = (word: string): string => {
  if (!word) return word;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
};
